import { centsToFormattedDollars } from '@fruitful/fruitils';
import {
    MembershipBillingFrequency,
    type SubscriptionProduct,
    SubscriptionProductId,
} from '@fruitful/types';
import { router } from 'expo-router';
import { Modal } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CloseButton,
    H6Emphasized,
    ScrollView,
    Spinner,
    useThemeColors,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { LinearGradient } from 'tamagui/linear-gradient';

import { useGetSubscriptionProductCatalog } from '../../billing/hooks/useSubscriptionProductCatalog';
import { usePremiumFrequencyPricing } from '../../billing/hooks/useUpgradeSubscription';
import { useActiveTheme } from '../../common/providers/DesignSystemProvider';
import {
    formatAumFeeLines,
    formatGeneralSessionsLabel,
    formatGuideMessagingLabel,
    formatPremiumCardLabel,
} from '../types/SubscriptionSummary';
import { BenefitsDisclosures } from './Subscription';

interface ComparisonRow {
    label: string;
    standard: string[];
    premium: string[];
}

const buildComparisonRows = (
    standard: SubscriptionProduct,
    premium: SubscriptionProduct,
    premiumFeeLines: string[],
): ComparisonRow[] => [
    {
        label: 'APY¹',
        standard: [standard.banking.rateCashAPY],
        premium: [premium.banking.rateCashAPY],
    },
    {
        label: 'Cash back²',
        standard: [standard.banking.rateCashBack],
        premium: [premium.banking.rateCashBack],
    },
    {
        label: 'AUM on investments³',
        standard: formatAumFeeLines(standard),
        premium: formatAumFeeLines(premium),
    },
    {
        label: 'Guide messaging',
        standard: [formatGuideMessagingLabel(standard)],
        premium: [formatGuideMessagingLabel(premium)],
    },
    {
        label: 'Live Guide sessions',
        standard: [formatGeneralSessionsLabel(standard)],
        premium: [formatGeneralSessionsLabel(premium)],
    },
    {
        label: 'Premium card',
        standard: [formatPremiumCardLabel(standard)],
        premium: [formatPremiumCardLabel(premium)],
    },
    {
        label: 'Membership fee',
        standard: [standard.isZeroDollarProduct ? '$0/yr' : '—'],
        premium: premiumFeeLines,
    },
];

const LABEL_COLUMN_FLEX = {
    flexGrow: 101,
    flexShrink: 1,
    flexBasis: 0,
} as const;
const VALUE_COLUMN_FLEX = {
    flexGrow: 117.5,
    flexShrink: 1,
    flexBasis: 0,
} as const;

const TOTAL_COLUMN_WIDTH = 101 + 117.5 + 117.5;
const LABEL_COLUMN_PERCENT = (101 / TOTAL_COLUMN_WIDTH) * 100;
const VALUE_COLUMN_PERCENT = (117.5 / TOTAL_COLUMN_WIDTH) * 100;
const PREMIUM_COLUMN_START_PERCENT =
    LABEL_COLUMN_PERCENT + VALUE_COLUMN_PERCENT;

const GOLD_GRADIENT_START: [number, number] = [0, 0.3];
const GOLD_GRADIENT_END: [number, number] = [1, 0.7];

const useGoldGradientColors = (): [string, string] => {
    const [activeTheme] = useActiveTheme();
    const { getPrimitiveColor } = useThemeColors();
    return [
        getPrimitiveColor('$peachLight', activeTheme),
        getPrimitiveColor('$peach', activeTheme),
    ];
};

interface PremiumUpgradeComparisonSheetProps {
    isOpen: boolean;
    onClose: () => void;
}

export const PremiumUpgradeComparisonSheet = ({
    isOpen,
    onClose,
}: PremiumUpgradeComparisonSheetProps) => (
    <Modal
        visible={isOpen}
        animationType="slide"
        presentationStyle="fullScreen"
        onRequestClose={onClose}
    >
        <PremiumUpgradeComparisonModalContent onClose={onClose} />
    </Modal>
);

const ModalHeader = ({
    onClose,
    paddingTop,
}: {
    onClose: () => void;
    paddingTop: number;
}) => (
    <XStack
        alignItems="center"
        justifyContent="space-between"
        paddingHorizontal={16}
        paddingTop={paddingTop}
        paddingBottom={16}
    >
        <View width={40} />
        <BodyMediumRegular flex={1} textAlign="center" color="$peachDarkest">
            Fruitful Premium
        </BodyMediumRegular>
        <CloseButton onPress={onClose} background="gray" />
    </XStack>
);

const PremiumUpgradeComparisonModalContent = ({
    onClose,
}: {
    onClose: () => void;
}) => {
    const { top, bottom } = useSafeAreaInsets();
    const goldGradientColors = useGoldGradientColors();

    return (
        <View flex={1}>
            <LinearGradient
                colors={goldGradientColors}
                start={GOLD_GRADIENT_START}
                end={GOLD_GRADIENT_END}
                position="absolute"
                zIndex={-1}
                top={0}
                left={0}
                right={0}
                bottom={0}
            />
            <YStack flex={1} paddingTop={top}>
                <ModalHeader onClose={onClose} paddingTop={16} />
                <ScrollView
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={{
                        alignItems: 'center',
                        paddingHorizontal: 16,
                        paddingBottom: Math.max(bottom, 40),
                    }}
                >
                    <View width="100%" maxWidth={440}>
                        <PremiumUpgradeComparisonContent onClose={onClose} />
                    </View>
                </ScrollView>
            </YStack>
        </View>
    );
};

const PremiumUpgradeComparisonContent = ({
    onClose,
}: {
    onClose: () => void;
}) => {
    const { data: catalog, isLoading: isCatalogLoading } =
        useGetSubscriptionProductCatalog({
            subscriptionProductIds: [
                SubscriptionProductId.STANDARD,
                SubscriptionProductId.PREMIUM,
            ],
        });

    const standard = catalog?.[SubscriptionProductId.STANDARD];
    const premium = catalog?.[SubscriptionProductId.PREMIUM];

    if (isCatalogLoading || standard == null || premium == null) {
        return <Spinner />;
    }

    return (
        <ComparisonTable
            standard={standard}
            premium={premium}
            onClose={onClose}
        />
    );
};

const ComparisonTable = ({
    standard,
    premium,
    onClose,
}: {
    standard: SubscriptionProduct;
    premium: SubscriptionProduct;
    onClose: () => void;
}) => {
    const goldGradientColors = useGoldGradientColors();
    const { pricing, isLoading: isPricingLoading } =
        usePremiumFrequencyPricing();
    const annualAmountCents =
        pricing[MembershipBillingFrequency.YEARLY]?.stripePreview
            .amountDueNowCents;
    const quarterlyAmountCents =
        pricing[MembershipBillingFrequency.QUARTERLY]?.stripePreview
            .amountDueNowCents;
    // Compliance: show both billing options, e.g. "$999/yr" + "or $250/qtr"
    const premiumFeeLines =
        annualAmountCents == null || quarterlyAmountCents == null
            ? ['…']
            : [
                  `${centsToFormattedDollars(annualAmountCents, false)}/yr`,
                  `or ${centsToFormattedDollars(quarterlyAmountCents, false)}/qtr`,
              ];

    const rows = buildComparisonRows(standard, premium, premiumFeeLines);

    return (
        <YStack gap={24} width="100%">
            <YStack gap={8} alignItems="center">
                <H6Emphasized color="$peachDarkest" textAlign="center">
                    Upgrade to Premium
                </H6Emphasized>
                <BodyMediumRegular
                    color="$peachDarkest"
                    opacity={0.8}
                    textAlign="center"
                >
                    Get one-on-one guidance and all Premium benefits by
                    upgrading your membership below.
                </BodyMediumRegular>
            </YStack>

            <YStack
                borderRadius={24}
                backgroundColor="rgba(255,255,255,0.5)"
                borderWidth={1}
                borderColor="$white"
                padding={16}
                gap={16}
                overflow="hidden"
            >
                <XStack width="100%">
                    <View {...LABEL_COLUMN_FLEX} />
                    <View {...VALUE_COLUMN_FLEX} alignItems="center">
                        <BodySmallEmphasized
                            textAlign="center"
                            color="$gray1200"
                        >
                            All Fruitful memberships
                        </BodySmallEmphasized>
                    </View>
                    <View {...VALUE_COLUMN_FLEX} alignItems="center">
                        <BodySmallEmphasized
                            textAlign="center"
                            color="$peachDarkest"
                        >
                            with Premium benefits
                        </BodySmallEmphasized>
                    </View>
                </XStack>

                <View position="relative" zIndex={0}>
                    <View
                        position="absolute"
                        zIndex={-1}
                        top={0}
                        bottom={0}
                        left={`${LABEL_COLUMN_PERCENT}%`}
                        width={`${VALUE_COLUMN_PERCENT}%`}
                        backgroundColor="$white"
                        borderTopLeftRadius={16}
                        borderBottomLeftRadius={16}
                    />
                    <View
                        position="absolute"
                        zIndex={-1}
                        top={0}
                        bottom={0}
                        left={`${PREMIUM_COLUMN_START_PERCENT}%`}
                        right={0}
                        overflow="hidden"
                        borderTopRightRadius={16}
                        borderBottomRightRadius={16}
                    >
                        <LinearGradient
                            colors={goldGradientColors}
                            start={GOLD_GRADIENT_START}
                            end={GOLD_GRADIENT_END}
                            width="100%"
                            height="100%"
                        />
                    </View>

                    <YStack gap={8} paddingTop={10} width="100%">
                        {rows.map((row, index) => (
                            <XStack
                                key={row.label}
                                width="100%"
                                alignItems="center"
                            >
                                <View
                                    {...LABEL_COLUMN_FLEX}
                                    paddingVertical={8}
                                    paddingRight={8}
                                >
                                    <BodySmallEmphasized color="$gray1200">
                                        {row.label}
                                    </BodySmallEmphasized>
                                </View>
                                <View
                                    {...VALUE_COLUMN_FLEX}
                                    alignItems="center"
                                    justifyContent="center"
                                    paddingVertical={8}
                                    paddingHorizontal={4}
                                >
                                    {row.standard.map((line) => (
                                        <BodySmallRegular
                                            key={line}
                                            textAlign="center"
                                            color="$gray1200"
                                        >
                                            {line}
                                        </BodySmallRegular>
                                    ))}
                                </View>
                                <View
                                    {...VALUE_COLUMN_FLEX}
                                    alignItems="center"
                                    justifyContent="center"
                                    paddingVertical={
                                        index === rows.length - 1 ? 12 : 8
                                    }
                                    paddingHorizontal={4}
                                >
                                    {row.premium.map((line) => (
                                        <BodySmallEmphasized
                                            key={line}
                                            textAlign="center"
                                            color="$peachDarkest"
                                        >
                                            {line}
                                        </BodySmallEmphasized>
                                    ))}
                                </View>
                            </XStack>
                        ))}
                    </YStack>
                </View>
            </YStack>

            <YStack gap={12} width="100%" maxWidth={400} alignSelf="center">
                <Button
                    text="Upgrade to Premium"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    disabled={isPricingLoading}
                    onPress={() => {
                        onClose();
                        router.push('/settings/membership/upgrade');
                    }}
                />
                <Button
                    text="Cancel"
                    variant={ButtonVariant.TERTIARY}
                    size={ButtonSize.LARGE}
                    onPress={onClose}
                />
            </YStack>
            <BenefitsDisclosures />
        </YStack>
    );
};
